/**
 * ArtworkPanelPlacement.js — Task #403
 *
 * Pure placement math for the floating museum-artwork panel
 * (SimpleImagePanel). Two jobs, both free of DOM/WebGL so they run
 * under plain Node for verification (headless browsers have no WebGL):
 *
 *   1. computeArtworkAnchor(model, charEntry)
 *      Per-character head anchor, recomputed on EVERY show so
 *      character swaps stay correct. Reuses the exact detection
 *      chain the camera framing uses (EyeFraming.js):
 *        explicit EyeAnchor node (per-character `useEyeAnchor`)
 *        → eye-node heuristics → head-bone / head-mesh /
 *        upper-third fallbacks.
 *      Also derives the character's facing direction from
 *      `modelRotationY` and the screen-left axis as seen from the
 *      default portrait camera (which sits along `forward`).
 *
 *   2. computeArtworkLayout(anchor, imageAspect, camera)
 *      Panel center/orientation/size + frame & placard proportions.
 *      The art is sized from the character's face scale (not a fixed
 *      80 world units), placed BESIDE the head on the current side
 *      (screen-left), at a depth close to the head's own depth plane
 *      so the pitched portrait camera no longer drags it down-screen.
 *      Wide landscape images are offset by half their width so the
 *      near edge keeps clearance from the face. A frustum-fit pass
 *      against the LIVE camera guarantees the piece is visible at
 *      the default portrait framing on desktop AND mobile aspect
 *      ratios without any camera change; on very narrow viewports it
 *      degrades gracefully ("tuck" mode: slightly deeper + closer,
 *      character occludes the near part, outer edge may run past the
 *      screen edge but a clear strip always remains beside the head).
 */
import * as THREE from "three";
import {
    detectExplicitEyeAnchor,
    detectEyeAnchors,
    fallbackAnchorsFromHead,
} from "./EyeFraming.js";

const UP = new THREE.Vector3(0, 1, 0);

export const ARTWORK_LAYOUT = Object.freeze({
    // Art height = faceHeight × factor (faceHeight ≈ IPD × 3.2, so a
    // typical character lands near the old fixed 80-unit height).
    HEIGHT_FACTOR: 3.8,
    HEIGHT_MIN: 30,
    HEIGHT_MAX: 112,
    // Fit floor before switching to tuck mode, × faceHeight.
    MIN_HEIGHT_FACTOR: 1.9,
    // Absolute floor when even tuck mode can't fit, × faceHeight.
    HARD_MIN_FACTOR: 1.2,
    // Face center → near FRAME edge, × faceWidth.
    CLEARANCE_FACTOR: 1.15,
    CLEARANCE_TUCKED: 0.8,
    CLEARANCE_FLOOR: 0.55,
    // Along the character's facing (negative = behind the eye plane).
    DEPTH_FACTOR: -0.35,
    DEPTH_TUCKED: -1.4,
    // Art center sits this × height below the eye line, so the top of
    // the frame lands near the top of the head instead of towering
    // over it (placard hangs below, keeping the block eye-centered).
    EYELINE_BIAS: -0.12,
    // Gallery chrome, proportional to art height (clamped in cm).
    FRAME_RATIO: 0.045,
    FRAME_MIN: 1.6,
    FRAME_MAX: 4.5,
    FILLET_RATIO: 0.012,
    PLACARD_H_RATIO: 0.16,
    PLACARD_H_MIN: 6,
    PLACARD_H_MAX: 12,
    PLACARD_GAP_RATIO: 0.05,
    PLACARD_GAP_MIN: 1.5,
    PLACARD_GAP_MAX: 5,
    // Fraction of the half-frustum the piece must stay inside.
    NDC_LIMIT: 0.94,
    ASPECT_MIN: 0.3,
    ASPECT_MAX: 3.0,
});

function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
}

/**
 * Per-character head anchor for artwork placement. Returns null when
 * no model / no detectable head (caller falls back to the legacy
 * fixed position).
 *
 * @param {THREE.Object3D} model     committed character model
 * @param {object|null}    charEntry characterData[modelId] (for
 *                                   useEyeAnchor / modelRotationY)
 */
export function computeArtworkAnchor(model, charEntry) {
    if (!model) return null;
    let anchors = null;
    if (charEntry && charEntry.useEyeAnchor) {
        anchors = detectExplicitEyeAnchor(model);
    }
    if (!anchors) anchors = detectEyeAnchors(model);
    if (!anchors) anchors = fallbackAnchorsFromHead(model);
    if (!anchors || !anchors.midpoint) return null;

    const rotY =
        charEntry && Number.isFinite(charEntry.modelRotationY)
            ? charEntry.modelRotationY
            : 0;
    // Character facing — same convention as EyeFraming's framing
    // computer (default camera sits along this direction).
    const forward = new THREE.Vector3(Math.sin(rotY), 0, Math.cos(rotY));
    // Screen-left as seen from the default portrait camera: forward × up.
    // (For rotY=0, forward=+Z, this is -X — the side the panel has
    // always lived on.)
    const side = new THREE.Vector3().crossVectors(forward, UP).normalize();

    return {
        midpoint: anchors.midpoint.clone(),
        eyeDistance: anchors.eyeDistance,
        faceHeight: anchors.faceHeight,
        faceWidth: anchors.faceWidth,
        headTopY: anchors.headTopY,
        source: anchors.source,
        rotationY: rotY,
        forward,
        side,
    };
}

/**
 * Probe the camera frustum at the panel's depth plane and solve the
 * three linear-in-height constraints (outer edge, top edge, placard
 * bottom). Returns null when the camera is unusable (missing, behind
 * the character, degenerate) — the caller then skips fitting.
 */
function probeFrustum(anchor, camera, depthBias, clearance, ratios) {
    if (!camera || !camera.isPerspectiveCamera) return null;
    try {
        camera.updateMatrixWorld(true);
    } catch (_e) {
        /* proceed with whatever matrix is there */
    }
    const camPos = new THREE.Vector3().setFromMatrixPosition(
        camera.matrixWorld,
    );
    const viewDir = new THREE.Vector3(0, 0, -1).transformDirection(
        camera.matrixWorld,
    );
    const rightCam = new THREE.Vector3(1, 0, 0).transformDirection(
        camera.matrixWorld,
    );
    const upCam = new THREE.Vector3(0, 1, 0).transformDirection(
        camera.matrixWorld,
    );

    // Hinge: the face center moved to the panel's depth plane.
    const hinge = anchor.midpoint
        .clone()
        .addScaledVector(anchor.forward, depthBias);
    const depthAlong = hinge.clone().sub(camPos).dot(viewDir);
    if (!(depthAlong > 1)) return null; // camera behind / on top of it

    const tanV = Math.tan(((camera.fov || 50) * Math.PI) / 360);
    const halfH = tanV * depthAlong * ARTWORK_LAYOUT.NDC_LIMIT;
    const halfW = halfH * (camera.aspect || 16 / 9);

    const frustumCenter = camPos.clone().addScaledVector(viewDir, depthAlong);
    const rel = hinge.clone().sub(frustumCenter);
    const hx = rel.dot(rightCam);
    const hy = rel.dot(upCam);

    // Which way the panel's side axis points on screen (≈ ±1).
    const sideDot = anchor.side.dot(rightCam);
    const s = sideDot < 0 ? -1 : 1;

    const { aspect, fbR, gapR, phR, biasR } = ratios;
    // Horizontal: |hx| + clearance + h·(aspect + 2·frame) ≤ halfW
    const spanR = aspect + 2 * fbR;
    const hH = (halfW - Math.abs(hx) - clearance) / spanR;
    // Vertical top: hy + h·(0.5 + frame + bias) ≤ halfH   (bias < 0)
    const topR = 0.5 + fbR + biasR;
    const hVtop = topR > 0.01 ? (halfH - hy) / topR : Infinity;
    // Vertical bottom (includes placard):
    //   hy − h·(0.5 + frame − bias + gap + placard) ≥ −halfH
    const botR = 0.5 + fbR - biasR + gapR + phR;
    const hVbot = (halfH + hy) / botR;

    return { hH, hVtop, hVbot, halfW, halfH, hx, hy, s, depthAlong };
}

/**
 * Compute the full panel layout beside the character's head.
 *
 * @param {object} anchor       output of computeArtworkAnchor
 * @param {number} imageAspect  texture width / height
 * @param {THREE.PerspectiveCamera|null} camera live camera (fit pass);
 *                              pass null to skip frustum fitting
 * @returns layout object (world units):
 *   { position, rotationY, height, width, frame, fillet,
 *     placardHeight, placardGap, clearance, depth, tucked, overflow,
 *     fitScale, screenSide, anchorSource }
 */
export function computeArtworkLayout(anchor, imageAspect, camera) {
    const L = ARTWORK_LAYOUT;
    if (!anchor || !anchor.midpoint) return null;

    const aspect = clamp(
        Number.isFinite(imageAspect) && imageAspect > 0 ? imageAspect : 1.5,
        L.ASPECT_MIN,
        L.ASPECT_MAX,
    );
    const faceH = Math.max(8, anchor.faceHeight || 20);
    const faceW = Math.max(10, anchor.faceWidth || 26);

    const desiredH = clamp(faceH * L.HEIGHT_FACTOR, L.HEIGHT_MIN, L.HEIGHT_MAX);
    const hMin = faceH * L.MIN_HEIGHT_FACTOR;
    const hHardMin = faceH * L.HARD_MIN_FACTOR;

    const ratios = {
        aspect,
        fbR: L.FRAME_RATIO,
        gapR: L.PLACARD_GAP_RATIO,
        phR: L.PLACARD_H_RATIO,
        biasR: L.EYELINE_BIAS,
    };

    let depth = faceH * L.DEPTH_FACTOR;
    let clearance = faceW * L.CLEARANCE_FACTOR;
    let tucked = false;
    let overflow = false;

    let probe = probeFrustum(anchor, camera, depth, clearance, ratios);
    let h = desiredH;
    if (probe) {
        h = Math.min(desiredH, probe.hH, probe.hVtop, probe.hVbot);
    }

    // Not enough room beside the head at head depth (narrow viewport /
    // wide panorama): tuck the piece slightly deeper and closer — the
    // character occludes the near part, which reads like the artwork
    // hanging just behind the shoulder.
    if (probe && h < hMin) {
        tucked = true;
        depth = faceH * L.DEPTH_TUCKED;
        clearance = faceW * L.CLEARANCE_TUCKED;
        probe = probeFrustum(anchor, camera, depth, clearance, ratios);
        if (probe) {
            h = Math.min(desiredH, probe.hH, probe.hVtop, probe.hVbot);
        } else {
            h = desiredH;
        }
    }

    // Still too tight (phone portrait): keep a readable piece at the
    // floor height, shrink clearance toward its floor, and accept the
    // OUTER edge running past the screen edge — a clear strip always
    // remains between the cheek and the viewport edge.
    if (probe && h < hMin) {
        h = clamp(
            Math.min(desiredH, probe.hVtop, probe.hVbot),
            hHardMin,
            hMin,
        );
        const spanNeeded = h * (aspect + 2 * L.FRAME_RATIO);
        const roomForClearance =
            probe.halfW - Math.abs(probe.hx) - spanNeeded;
        const floorC = faceW * L.CLEARANCE_FLOOR;
        clearance = Math.max(floorC, roomForClearance);
        overflow = roomForClearance < floorC - 1e-6;
    }

    if (!Number.isFinite(h) || h <= 0) h = desiredH;
    h = clamp(h, Math.min(hHardMin, desiredH), desiredH);

    const width = h * aspect;
    const frame = clamp(h * L.FRAME_RATIO, L.FRAME_MIN, L.FRAME_MAX);
    const fillet = Math.max(0.6, h * L.FILLET_RATIO);
    const placardHeight = clamp(
        h * L.PLACARD_H_RATIO,
        L.PLACARD_H_MIN,
        L.PLACARD_H_MAX,
    );
    const placardGap = clamp(
        h * L.PLACARD_GAP_RATIO,
        L.PLACARD_GAP_MIN,
        L.PLACARD_GAP_MAX,
    );

    // Face center → art center along the side axis. `clearance` is the
    // gap to the near FRAME edge, so the art center adds frame + w/2 —
    // this is what keeps a wide landscape's near edge clear of the
    // face (the piece is pushed out by half its own width).
    const centerOff = clearance + frame + width / 2;
    const position = anchor.midpoint
        .clone()
        .addScaledVector(anchor.side, centerOff)
        .addScaledVector(anchor.forward, depth);
    position.y += h * L.EYELINE_BIAS;

    return {
        position,
        rotationY: anchor.rotationY || 0,
        height: h,
        width,
        frame,
        fillet,
        placardHeight,
        placardGap,
        clearance,
        depth,
        tucked,
        overflow,
        fitScale: h / desiredH,
        // The panel always sits on the default camera's screen-left
        // (side = forward × up). Consumers use this for e.g. the
        // reveal glance direction.
        screenSide: "left",
        anchorSource: anchor.source,
    };
}
